import React, { useRef, useEffect, useCallback, forwardRef } from 'react';
import styled from 'styled-components';

const StyledCellContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  outline: ${props => props.isFocused ? '2px solid var(--color-primary)' : 'none'};
  outline-offset: -2px;
  background-color: ${props => props.isFocused ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  transition: all 0.15s ease;
  
  &:focus-within {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    background-color: rgba(59, 130, 246, 0.1);
  }
`;

// Создаем обычный div компонент с forwardRef
const CellContainer = forwardRef(({ isFocused, children, ...props }, ref) => (
    <StyledCellContainer ref={ref} isFocused={isFocused} {...props}>
        {children}
    </StyledCellContainer>
));

const NavigationWrapper = ({
    children,
    rowIndex,
    colIndex,
    navigation,
    ...props
}) => {
    const cellRef = useRef(null);

    // Проверяем готовность навигации
    const isNavigationReady = navigation.isTableReady && navigation.isInitialized;

    const isFocused = isNavigationReady &&
        navigation.focusedCell.rowIndex === rowIndex &&
        navigation.focusedCell.colIndex === colIndex;

    // Регистрируем ref клетки при изменении готовности навигации
    useEffect(() => {
        if (!isNavigationReady) {
            navigation.registerCellRef(rowIndex, colIndex, null);
            return;
        }

        if (cellRef.current) {
            navigation.registerCellRef(rowIndex, colIndex, cellRef.current);
        }

        return () => navigation.registerCellRef(rowIndex, colIndex, null);
    }, [rowIndex, colIndex, navigation, isNavigationReady]);

    // Добавляем обработчик клавиш к input элементам внутри клетки
    useEffect(() => {
        if (!isNavigationReady || !cellRef.current) return;

        const cellElement = cellRef.current;

        // Проверяем, что это DOM элемент
        if (cellElement && cellElement.nodeType === Node.ELEMENT_NODE) {
            const inputs = cellElement.querySelectorAll('input, textarea');

            const handleKeyDown = (event) => {
                navigation.handleCellKeyDown(event);
            };

            inputs.forEach(input => {
                input.addEventListener('keydown', handleKeyDown);
            });

            return () => {
                inputs.forEach(input => {
                    input.removeEventListener('keydown', handleKeyDown);
                });
            };
        }
    }, [navigation.handleCellKeyDown, isNavigationReady]);

    return (
        <CellContainer
            ref={cellRef}
            isFocused={isFocused}
            {...props}
        >
            {children}
        </CellContainer>
    );
};

export default NavigationWrapper;
